import CalendarIcon from "assets/images/Calendar";
import ClockIcon from "assets/images/Clock";
import {CalendarEvent, google, ics} from "calendar-link";
import {format} from "date-fns";
import {useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {appointmentDetails as strings} from "strings";

import Button from "components/Button";
import CancelAppointmentModal from "components/CancelAppointmentModal";
import Divider from "components/Divider";
import MobileNavigationHeader from "components/MobileNavigationHeader";
import StyledModal from "components/StyledModal";

import {useGetAvailableAppointmentCampaignsByDate} from "liberty-core/api/react-query/appointment";

import {useAuthenticatedUser} from "lib/auth";
import {outlookLink} from "lib/calendar";
import {useCurrentPharmacy} from "lib/useCurrentPharmacy";

import styles from "./AppointmentDetailsPage.module.css";

type LocationState = {
  selectedPatientId: string;
  campaignName: string;
  campaignDescription?: string;
  optionName?: string;
  scheduledDateTime: string;
  addToVisit: boolean;
};

export default function AppointmentDetailsPage() {
  const location = useLocation();
  const locationState = location.state as LocationState;
  const selectedPatientId = locationState.selectedPatientId;
  const campaignName = locationState.campaignName;
  const campaignDescription = locationState.campaignDescription;
  const optionName = locationState.optionName;
  const scheduledDateTime = locationState.scheduledDateTime;
  const addToVisit = locationState.addToVisit;
  const appointmentId = useParams().id ?? "";
  const [pharmacy] = useCurrentPharmacy();

  const navigate = useNavigate();

  const [authenticatedUser] = useAuthenticatedUser();
  const patient = authenticatedUser?.data?.linkedPatients?.find(
    (linkedPatient) => linkedPatient.patientId === selectedPatientId,
  );
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);

  const calendarEvent: CalendarEvent = {
    title: campaignName,
    start: scheduledDateTime,
    duration: [1, "hour"],
    location: pharmacy?.name,
  };

  const {data: additionalApptCampaigns, isLoading} =
    useGetAvailableAppointmentCampaignsByDate(
      pharmacy?.id ?? 0,
      scheduledDateTime,
      {enabled: addToVisit === true},
    );

  return (
    <div>
      <MobileNavigationHeader backTo="/appointments" />
      {authenticatedUser?.data.linkedPatients?.length ? (
        <>
          <div className={styles.title}>{strings.appointmentDetails}</div>
          <div className={styles.patientName}>
            {patient
              ? patient.firstName + " " + patient.lastName
              : authenticatedUser?.data.name}
          </div>
        </>
      ) : (
        <div className={styles.patientName}>{authenticatedUser?.data.name}</div>
      )}
      <Divider className={styles.divider} />
      <div className={styles.appointmentName}>
        {optionName} - {campaignName}
      </div>
      <div>{campaignDescription}</div>
      <Divider className={styles.divider} />
      <div className={styles.detailsRow}>
        <div className={styles.detailsRowTitle}>
          <div className={styles.detailsIcon}>
            <CalendarIcon />
          </div>

          <div className={styles.detailsText}>{strings.date}</div>
        </div>
        <div className={styles.detailsText}>
          {format(new Date(scheduledDateTime), "MM/dd/yy")}
        </div>
      </div>
      <Divider className={styles.divider} />
      <div className={styles.detailsRow}>
        <div className={styles.detailsRowTitle}>
          <div className={styles.detailsIcon}>
            <ClockIcon />
          </div>

          <div className={styles.detailsText}>{strings.time}</div>
        </div>
        <div className={styles.detailsText}>
          {format(new Date(scheduledDateTime), "hh:mm aaa")}
        </div>
      </div>
      <Divider className={styles.divider} />
      {addToVisit && (
        <Button
          onClick={() => {
            navigate("/appointments/select-campaign", {
              state: {
                appointmentType: "Vaccination",
                selectedPatientId: selectedPatientId,
                apptCampaigns: additionalApptCampaigns ?? [],
                startDateTime: scheduledDateTime,
              },
            });
          }}
          variant="unfilled"
          disabled={
            isLoading ||
            !additionalApptCampaigns ||
            additionalApptCampaigns.length === 0
          }
          className={styles.addVaccineButton}
        >
          {strings.addVaccine}
        </Button>
      )}
      <div className={styles.buttonRow}>
        <Button
          className={styles.cancelAppointmentButton}
          variant="filled"
          onClick={() => setShowCancelModal(true)}
        >
          {strings.cancelAppointment}
        </Button>
        <Button onClick={() => setShowCalendarModal(true)}>
          {strings.addToCalendar}
        </Button>
      </div>
      <CancelAppointmentModal
        visible={showCancelModal}
        onClickClose={() => setShowCancelModal(false)}
        appointmentId={appointmentId}
        patientId={selectedPatientId}
      />
      <StyledModal
        visible={showCalendarModal}
        onRequestClose={() => setShowCalendarModal(false)}
      >
        <>
          <Button
            className={styles.calendarButton}
            href={google(calendarEvent)}
            variant="unfilled"
            target="_blank"
            rel="noopener noreferrer"
          >
            {strings.addToGoogleCalendar}
          </Button>
          <Button
            className={styles.calendarButton}
            href={outlookLink(
              scheduledDateTime,
              campaignName,
              pharmacy?.name ?? "",
            )}
            variant="unfilled"
            target="_blank"
            rel="noopener noreferrer"
          >
            {strings.addToOutlookCalendar}
          </Button>
          <Button
            className={styles.calendarButton}
            href={ics(calendarEvent)}
            variant="unfilled"
          >
            {strings.addToICalCalendar}
          </Button>
        </>
      </StyledModal>
    </div>
  );
}
